import { useState, useEffect } from "react";
import Header from "@/components/Header";
import AIConfidenceScore from "@/components/AIConfidenceScore";
import Features from "@/components/Features";
import PlatformTechnicalFeatures from "@/components/PlatformTechnicalFeatures";
import CloudProviders from "@/components/CloudProviders";
import HowItWorks from "@/components/HowItWorks";
import Testimonials from "@/components/Testimonials";
import FinalCTA from "@/components/FinalCTA";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { ArrowRight, Server, Paintbrush, Smartphone } from "lucide-react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTE_SEO } from "@/lib/seo-config";

const homeSEO = ROUTE_SEO['/'];

export default function Index() {
  const [userInput, setUserInput] = useState("");
  const [projectMode, setProjectMode] = useState<"design" | "fullstack" | "mobile">("fullstack");
  const [isFocused, setIsFocused] = useState(false);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();

  const prompts = [
    "Build a real-time collaborative task manager with drag-and-drop, team chat, and AI-powered task suggestions",
    "Create an e-commerce platform with inventory management, payment processing, and customer analytics dashboard",
    "Design a social media dashboard with live analytics, engagement tracking, and automated content scheduling",
    "Develop a fitness tracking app with workout plans, progress charts, nutrition logging, and AI coaching",
    "Make a project management tool with kanban boards, time tracking, resource allocation, and team collaboration",
  ];

  // All hooks must run before any early returns (Rules of Hooks)
  useEffect(() => {
    if (isAuthenticated || isFocused || userInput) return;

    const currentPrompt = prompts[currentPromptIndex];

    if (isTyping) {
      if (displayedText.length < currentPrompt.length) {
        const timeout = setTimeout(() => {
          setDisplayedText(currentPrompt.slice(0, displayedText.length + 1));
        }, 30);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
        return () => clearTimeout(timeout);
      }
    } else {
      const timeout = setTimeout(() => {
        setDisplayedText("");
        setCurrentPromptIndex((prev) => (prev + 1) % prompts.length);
        setIsTyping(true);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [displayedText, isTyping, currentPromptIndex, isFocused, userInput, prompts, isAuthenticated]);

  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/~" replace />;

  const activeTabBorderClass = isFocused ? "border-rose-300" : "border-warm-cream-300/60";

  const handleBuildApp = (e: React.FormEvent) => {
    e.preventDefault();
    // window.open("https://joylo.io", "_blank");
    if (userInput.trim()) {
      sessionStorage.setItem("joylo_initial_prompt", userInput);
    }
    sessionStorage.setItem("joylo_initial_project_mode", projectMode);
    navigate("/~");
  };

  return (
    <>
      <SEO
        title={homeSEO.title}
        description={homeSEO.description}
        canonicalUrl={homeSEO.canonicalUrl}
        ogImage={homeSEO.ogImage}
        structuredData={homeSEO.structuredData}
      />

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section - Sophisticated & Premium */}
        <section className="relative overflow-hidden pt-24 sm:pt-28 lg:pt-32 pb-8 sm:pb-12" role="banner" aria-label="Hero section">
          {/* Subtle background decoration - aurora-inspired gradients */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            {/* Soft gradient orbs - much more subtle on mobile */}
            <div className="absolute top-20 sm:top-0 left-1/4 w-[200px] h-[200px] sm:w-[500px] sm:h-[500px] lg:w-[600px] lg:h-[600px] bg-gradient-to-br from-sunset-coral-200/[0.08] sm:from-sunset-coral-200/20 to-transparent rounded-full blur-3xl animate-float-gentle"></div>
            <div className="absolute bottom-0 right-1/4 w-[180px] h-[180px] sm:w-[400px] sm:h-[400px] lg:w-[500px] lg:h-[500px] bg-gradient-to-tl from-soft-lavender-200/[0.08] sm:from-soft-lavender-200/20 to-transparent rounded-full blur-3xl animate-float-gentle" style={{ animationDelay: '-3s' }}></div>
          </div>

          <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            {/* Hero Text - centered on all viewports */}
            <div className="text-center mb-8 sm:mb-10">
              {/* Opus 4.8 announcement badge - always centered */}
              <div className="text-center mb-5 sm:mb-6">
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 glass rounded-full text-[11px] sm:text-xs font-semibold text-ink-blue-900 shadow-elegant animate-fade-in-up-elegant">
                  <div className="w-1.5 h-1.5 rounded-full bg-gradient-sunset animate-pulse flex-shrink-0" aria-hidden="true" />
                  <span className="sm:hidden">Powered by Claude Opus 4.8</span>
                  <span className="hidden sm:inline">Now powered by Claude Opus 4.8 &mdash; Anthropic's most intelligent model</span>
                </div>
              </div>

              <h1 className="font-sans text-[2.5rem] leading-[1.1] sm:text-5xl md:text-6xl lg:text-7xl font-bold text-ink-blue-900 mb-4 sm:mb-5 tracking-tight">
                We finish what{" "}
                <span className="text-gradient-sunset">others start.</span>
              </h1>

              <div className="relative inline-block mb-8">
   
                  {/* MOAT Text */}
                  <p className="text-base md:text-lg lg:text-xl font-semibold text-ink-blue-900 leading-snug">
                    Build with AI. Scale with certified {" "}
                    <span className="text-gradient-sunset font-bold">
                      Joylo Architects.
                    </span>
                  </p>
         
              </div>

              {/* Free tier trust signals - same 3 at every width */}
              <div className="flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-6 gap-y-2 text-xs sm:text-sm font-medium text-soft-gray-700">
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-sunset flex-shrink-0" aria-hidden="true"></span>
                  No credit card
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-sunset flex-shrink-0" aria-hidden="true"></span>
                  <span className="sm:hidden">30s setup</span>
                  <span className="hidden sm:inline">Setup in 30 seconds</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-gradient-sunset flex-shrink-0" aria-hidden="true"></span>
                  No DevOps
                </span>
              </div>
            </div>

            {/* Prompt Box - Clean & Sophisticated */}
            <div className="w-full max-w-4xl mx-auto">
              {/* Tabs Row - Equal columns on mobile, natural left-aligned on sm+ */}
              <div className="grid grid-cols-3 sm:flex sm:items-end items-end relative z-10">
                <button
                  type="button"
                  onClick={() => setProjectMode('fullstack')}
                  className={`
                    relative px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-t-xl border border-b-0 transition-[color,background-color,border-color,box-shadow] duration-300 ease-out flex items-center justify-center gap-1.5 sm:gap-2
                    ${projectMode === 'fullstack'
                      ? `z-20 bg-white text-ink-blue-900 -mb-px pb-[7px] sm:pb-[9px] ${activeTabBorderClass} shadow-[0_-2px_8px_rgba(10,14,39,0.04)]`
                      : 'border-transparent bg-transparent text-soft-gray-500 hover:text-ink-blue-900 hover:bg-white/40 mb-0'
                    }
                  `}
                >
                  <Server className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>App</span>
                </button>

                <button
                  type="button"
                  onClick={() => setProjectMode('design')}
                  className={`
                    relative px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-t-xl border border-b-0 transition-[color,background-color,border-color,box-shadow] duration-300 ease-out flex items-center justify-center gap-1.5 sm:gap-2
                    ${projectMode === 'design'
                      ? `z-20 bg-white text-ink-blue-900 -mb-px pb-[7px] sm:pb-[9px] ${activeTabBorderClass} shadow-[0_-2px_8px_rgba(10,14,39,0.04)]`
                      : 'border-transparent bg-transparent text-soft-gray-500 hover:text-ink-blue-900 hover:bg-white/40 mb-0'
                    }
                  `}
                >
                  <Paintbrush className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Design</span>
                </button>

                <button
                  type="button"
                  onClick={() => setProjectMode('mobile')}
                  className={`
                    relative px-3 sm:px-5 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-t-xl border border-b-0 transition-[color,background-color,border-color,box-shadow] duration-300 ease-out flex items-center justify-center gap-1.5 sm:gap-2
                    ${projectMode === 'mobile'
                      ? `z-20 bg-white text-ink-blue-900 -mb-px pb-[7px] sm:pb-[9px] ${activeTabBorderClass} shadow-[0_-2px_8px_rgba(10,14,39,0.04)]`
                      : 'border-transparent bg-transparent text-soft-gray-500 hover:text-ink-blue-900 hover:bg-white/40 mb-0'
                    }
                  `}
                >
                  <Smartphone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span>Mobile</span>
                </button>
              </div>

              <div className="relative bg-white rounded-b-2xl overflow-hidden shadow-elegant-lg border border-warm-cream-300/60 transition-[border-color,box-shadow] duration-300 ease-out hover:shadow-elegant-xl focus-within:border-rose-300 focus-within:shadow-elegant-xl">
                {/* Input Area */}
                <form onSubmit={handleBuildApp} className="relative" role="search" aria-label="App builder prompt">
                  <div className="relative min-h-[72px] sm:min-h-[80px] px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
                    <label htmlFor="app-prompt" className="sr-only">Describe your app idea</label>
                    <textarea
                      id="app-prompt"
                      name="app-prompt"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      aria-label="Describe your app idea"
                      className="w-full h-full min-h-[72px] sm:min-h-[80px] bg-transparent border-none outline-none resize-none text-sm sm:text-base font-normal text-ink-blue-900"
                      style={{ caretColor: 'hsl(4 86% 68%)' }}
                    />
                    {!isFocused && !userInput && !displayedText && (
                      <div className="absolute inset-0 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 pointer-events-none" aria-hidden="true">
                        <div className="text-sm sm:text-base font-normal text-soft-gray-400">
                          Describe your app idea...
                        </div>
                      </div>
                    )}
                    {!isFocused && !userInput && displayedText && (
                      <div className="absolute inset-0 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 pointer-events-none overflow-hidden" aria-hidden="true">
                        <div className="text-sm sm:text-base font-normal text-soft-gray-500 line-clamp-2 sm:line-clamp-none">
                          {displayedText}
                          <span className="inline-block w-0.5 h-4 sm:h-5 bg-gradient-sunset ml-1 animate-pulse"></span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Bar */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 px-4 sm:px-6 lg:px-8 py-3 sm:py-3.5 bg-warm-cream-50/70 border-t border-warm-cream-200">
                    <div className="hidden sm:flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gradient-sunset animate-pulse flex-shrink-0" aria-hidden="true"></div>
                      <span className="text-xs sm:text-sm text-soft-gray-600 font-medium tracking-tight">Free to start &middot; No credit card</span>
                    </div>
                    <button
                      type="submit"
                      aria-label="Build free with Joylo — no credit card required"
                      className="group w-full sm:w-auto px-5 sm:px-6 py-2.5 bg-gradient-sunset hover:opacity-95 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-[0_4px_12px_rgba(244,114,114,0.25)] hover:shadow-[0_6px_20px_rgba(244,114,114,0.35)] hover:-translate-y-px active:translate-y-0 active:shadow-[0_2px_8px_rgba(244,114,114,0.2)] flex items-center justify-center gap-1.5"
                    >
                      Build Free
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" />
                    </button>
                  </div>
                </form>
              </div>

            </div>
          </div>
        </section>

        {/* Rest of the sections */}
        <AIConfidenceScore />
        <Features />
        <PlatformTechnicalFeatures />
        <HowItWorks />
        <Testimonials />
        <CloudProviders />
        <FinalCTA />
        <Footer />
      </div>
    </>
  );
}
